import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { playTone } from "../lib/sound";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Bell, Check, CheckCheck, AlertTriangle, Info } from "lucide-react";

const PRIORITY_ORDER = { normal: 0, important: 1, critical: 2 };

function timeAgo(iso, tz) {
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString("en-US", { timeZone: tz || "America/Chicago", month: "short", day: "numeric" });
}

function SeverityDot({ severity }) {
  const map = {
    critical: "bg-red-500",
    important: "bg-amber-500",
    normal: "bg-slate-300",
  };
  return <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${map[severity] || map.normal}`} />;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const prefsRef = useRef(null);
  const seenRef = useRef(new Set());
  const primed = useRef(false);

  const loadPrefs = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/preferences");
      prefsRef.current = data;
    } catch (e) { /* ignore */ }
  }, []);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
      const prefs = prefsRef.current;
      // detect new notifications for audible alert
      const newest = data.items.filter((n) => !seenRef.current.has(n.id));
      if (primed.current && prefs && prefs.audible && prefs.audible_device_enabled && !prefs.muted) {
        for (const n of newest) {
          if (PRIORITY_ORDER[n.severity] >= PRIORITY_ORDER[prefs.audible_min_priority || "important"]) {
            playTone(n.severity, prefs.volume ?? 0.7);
            break;
          }
        }
      }
      data.items.forEach((n) => seenRef.current.add(n.id));
      primed.current = true;
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    loadPrefs();
    poll();
    const id = setInterval(poll, 15000);
    return () => clearInterval(id);
  }, [loadPrefs, poll]);

  const markRead = async (n) => {
    if (!n.read_at) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
    }
  };

  const openNotification = async (n) => {
    await markRead(n);
    setOpen(false);
    await poll();
    if (n.advisory_id) navigate(`/advisories/${n.advisory_id}`);
    else if (n.handoff_id) navigate(`/handoffs/${n.handoff_id}`);
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); await poll(); } catch (e) { /* ignore */ }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) poll(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notification-unread-count" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-center">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="font-display text-sm font-bold text-slate-900">Notifications</p>
          <button data-testid="mark-all-read" onClick={markAll} className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
            <CheckCheck className="h-3.5 w-3.5" /> Mark all read
          </button>
        </div>
        <ScrollArea className="max-h-96">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400">You're all caught up.</div>
          ) : (
            <div className="divide-y divide-slate-50">
              {items.map((n) => (
                <div
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  className={`flex cursor-pointer gap-3 px-4 py-3 transition-colors hover:bg-slate-50 ${n.read_at ? "opacity-70" : "bg-blue-50/40"}`}
                >
                  <SeverityDot severity={n.severity} />
                  <div className="min-w-0 flex-1" onClick={() => openNotification(n)}>
                    <div className="flex items-center gap-1.5">
                      {n.severity === "critical" ? <AlertTriangle className="h-3.5 w-3.5 text-red-500" /> : n.severity === "important" ? <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> : <Info className="h-3.5 w-3.5 text-slate-400" />}
                      <p className="truncate text-sm font-semibold text-slate-900">{n.title}</p>
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{n.safe_summary}</p>
                    <p className="mt-1 text-[11px] text-slate-400">
                      {n.division ? `${n.division} · ` : ""}{timeAgo(n.created_at, user?.org_timezone)}
                      {!n.read_at && <span className="ml-1 font-semibold text-blue-600">• Unread</span>}
                    </p>
                  </div>
                  {!n.read_at && (
                    <button data-testid={`notification-read-${n.id}`} onClick={(e) => { e.stopPropagation(); markRead(n).then(poll); }} className="mt-0.5 h-fit rounded p-1 text-slate-400 hover:text-blue-600" title="Mark as read">
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        <div className="border-t border-slate-100 px-4 py-2 text-center">
          <button data-testid="notification-preferences-link" onClick={() => { setOpen(false); navigate("/preferences"); }} className="text-xs font-medium text-slate-500 hover:text-slate-900">
            Notification preferences
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
